import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useGridFilter } from 'ag-grid-react';
import MultiSelectComponent from './components/MultiSelectComponent';
import constant from '../../../../../appConstants';
import { isNumber } from 'lodash';
import NumericFilters from './components/numericOperatorFilter';

const MultiSelectFilter = (props) => {
  const { model, onModelChange } = props;

  const modelAsArray = useMemo(() => model || [], [model]);
  
  const numericFilterModel = useMemo(
    () => modelAsArray.find((item) => item?.numericOperator),
    [modelAsArray]
  );

  const { numericOperator, startRange, endRange } = numericFilterModel || {};
  
  //Only options without numeric operator
  const initialSelectedOptions = useMemo(
    () => modelAsArray.filter((item) => !item?.numericOperator),
    [modelAsArray]
  );

  const [state, setState] = useState({
    options: [],
    filteredOptions: [],
    selectedOptions: initialSelectedOptions,
    searchTerm: '',
    numericOperator: numericOperator || NumericFilters[0],
    startRange: startRange || '',
    endRange: endRange || ''
  });

  const isNumericDataRef = useRef(false);
  const timeoutRef = useRef(null);

  const isNumericFilterValid = useCallback(() => {
    return (
      isNumericDataRef.current &&
      !isNaN(parseInt(state.startRange)) &&
      (state.numericOperator.needsRange ? !isNaN(parseInt(state.endRange)) : true)
    );
  }, [state.startRange, state.endRange, state.numericOperator]);

  // The grid calls this to knoe if filter icon on header should be shown, Return true to show
  const isFilterActive = useCallback(
    (model) => {
      return model?.length > 0 || isNumericFilterValid();
    },
    [isNumericFilterValid]
  );

  const notifyFilterChanged = useCallback(
    (model) => {
      onModelChange(isFilterActive(model) ? model : null);
    },
    [onModelChange, isFilterActive]
  );

  useEffect(() => {
    const modelToSave = [
      ...state.selectedOptions,
      isNumericFilterValid()
        ? {
            startRange: state.startRange,
            endRange: state.endRange,
            numericOperator: state.numericOperator
          }
        : null
    ].filter(Boolean);
    notifyFilterChanged(modelToSave);
  }, [
    state.startRange,
    state.endRange,
    state.numericOperator,
    state.selectedOptions,
    notifyFilterChanged,
    isNumericFilterValid
  ]);

  // Update selectedOptions when options change (similar to old filter's onFilterApplied)
  useEffect(() => {
    const selectedOptions = state.options.filter((option) => option.checked);
    setState((prev) => ({
      ...prev,
      selectedOptions
    }));
  }, [state.options]);

  useEffect(() => {
    // If the model is cleared, reset the filter state
    if (!modelAsArray || modelAsArray.length === 0) {
      setState((prevState) => ({
        ...prevState,
        selectedOptions: [],
        startRange: '',
        endRange: '',
        numericOperator: NumericFilters[0],
        searchTerm: prevState.searchTerm,
      }));
    }
  }, [modelAsArray, NumericFilters]);

  const getUniqueSortedValues = useCallback(
    (array) => {
      array = [...new Set([...array])]; // creating new set remove duplicate values from array
      const intialLength = array.length;
      isNumericDataRef.current = true;
      array = array
        .filter((element) => isNumber(element) || element)
        .map((element) => {
          if (isNumericDataRef.current && isNaN(element)) {
            isNumericDataRef.current = false;
          }
          return element;
        }); // get formatted value
      array.sort(); //sorts string value
      array.sort((currentValue, nextValue) => currentValue - nextValue); // sorts numeric values
      isNumericDataRef.current = array.length ? isNumericDataRef.current : false;
      // Override the filter type if specified in the config , in filterParams.filterType
      if (props.filterType) {
        isNumericDataRef.current = props.filterType === constant.FILTER_TYPES.NUMBER;
      }

      if (intialLength > array.length) {
        array.push('');
      }
      return array;
    },
    [props.filterType]
  );

  // Load options for multiselector from values in grid for that column
  const loadOptions = useCallback(() => {
    let options = [];
    if (!props.api) return;

    props.api.forEachNode((node) => {
      const value = props.getValue(node);
      if(props.isValueList) {
        const field = props.colDef.field;
        const data = node.data[field];
        options.push(...data);
      } else {
        options.push(value);
      }
    });
    options = getUniqueSortedValues(options.map((option) => option?.label ?? option));
    options = options.map((option) => {
      const opt = state.selectedOptions.find((opt) => opt.value === option);
      return {
        label: option === '' ? constant.BLANK : option,
        value: option,
        checked: opt ? opt.checked : false
      };
    });
    const blankOptionExists = options.some((option) => option.value === '');
    if (!blankOptionExists && props.addDefaultBlank) {
      const isAlreadyChecked = state.options.find((item) => item.label === constant.BLANK)?.checked;
      options.push({
        label: constant.BLANK,
        value: '',
        checked: isAlreadyChecked
      });
    }
    setState((prev) => ({
      ...prev,
      options: options.sort(),
      selectedOptions: state.selectedOptions
    }));
  }, [state.selectedOptions, props.api, props.getValue, props.addDefaultBlank, getUniqueSortedValues]);

  const resetSearch = useCallback(() => {
    setState((prev) => ({
      ...prev,
      filteredOptions: prev.options,
      searchTerm: ''
    }));
  }, []);

  // Grid asks each active filter if each row in grid passes. Return true if row passes the filter. If false then it is removed the final set of filtered rows
  // params contains the node attributes and data object for a row
  const doesFilterPass = useCallback(
    ({ node }) => {
      if(props.isValueList) {
        return isValueListFilterPass(node);
      }
      let currentValue = props.getValue(node);
      // if value is not a number & is not valid value(undefined, null,"") assign "" as value, so that it can filtered in blank selection
      if (!isNumber(currentValue) && !Boolean(currentValue)) {
        currentValue = '';
      }

      if (isNumericDataRef.current && !isNaN(parseInt(currentValue))) {
        const { startRange, endRange, numericOperator } = state;
        return (
          numericOperator?.filter(Number(currentValue), startRange, endRange) ||
          state.selectedOptions.findIndex((option) => option.value === currentValue) > -1
        );
      }
      return (
        state.selectedOptions.findIndex(
          (option) =>
            option.value === currentValue ||
            option.value.toString().toUpperCase() === currentValue.toString().toUpperCase()
        ) > -1
      );
    },
    [props.getValue, state.selectedOptions, state.startRange, state.endRange, state.numericOperator]
  );

  const isValueListFilterPass = useCallback(
    (node) => {
      const field = props.colDef.field;
      const data = node.data[field];
      return state.selectedOptions.some((selectedOption) =>
        data.some((val) => checkIfValuesEqual(selectedOption.value, val))
      );
    },
    [props.colDef.field, state.selectedOptions, checkIfValuesEqual]
  );

  const checkIfValuesEqual = useCallback((value1, value2) => {
    return value1 == value2 || value1.toString().toUpperCase() === value2.toString().toUpperCase();
  }, []);

  const afterGuiAttached = useCallback(() => {
    loadOptions();
    resetSearch();
  }, [loadOptions, resetSearch]);

  const onNewRowsLoaded = useCallback(() => {
    // setTimeout is needed because the rowdata is not available immediately for loadOptions
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      loadOptions();
      resetSearch();
    }, 0);
  }, [loadOptions, resetSearch]);

  useEffect(() => {
    return () => {
        clearTimeout(timeoutRef.current);
    }
  }, [])

  // Register the filter with AG Grid
  useGridFilter({
    doesFilterPass,
    onNewRowsLoaded,
    afterGuiAttached
  });

  const onSelectAll = useCallback((checked) => {
    setState((prevState) => {
      // Create a map of which options should be checked based on filtered view
      const filteredValues = new Set(prevState.filteredOptions.map(opt => opt.value));
      
      // Update options - only check/uncheck the ones that are currently filtered
      const updatedOptions = prevState.options.map((option) => {
        if (filteredValues.has(option.value)) {
          return { ...option, checked };
        }
        return option; // Keep existing checked state for non-filtered options
      });
      
      // Update filteredOptions to show the new checked state
      const updatedFilteredOptions = prevState.filteredOptions.map((option) => ({
        ...option,
        checked
      }));
      
      return {
        ...prevState,
        filteredOptions: updatedFilteredOptions,
        options: updatedOptions
      };
    });
  }, []);

  const onOptionSelected = useCallback((option) => {
    setState((prevState) => {
      const selectedValue = option.value;
      const checked = option.checked;
      const updatedFilteredOptions = prevState.filteredOptions.map((opt) =>
        opt.value === selectedValue ? { ...opt, checked } : opt
      );
      const updatedOptions = prevState.options.map((opt) =>
        opt.value === selectedValue ? { ...opt, checked } : opt
      );
      return {
        ...prevState,
        filteredOptions: updatedFilteredOptions,
        options: updatedOptions
      };
    });
  }, []);

  const onSearch = useCallback((searchValue) => {
    setState((prev) => {
      const filteredOptions = prev.options.filter((option) =>
        option.value?.replace(/\s+/g, ' ').trim().toUpperCase().includes(searchValue.toUpperCase())
      );
      return {
        ...prev,
        filteredOptions,
        searchTerm: searchValue
      };
    });
  }, []);

  const numericOpertaorUpdated = useCallback((numericOperator) => {
    setState((prev) => ({
      ...prev,
      numericOperator
    }));
  }, []);

  const numericRangeUpdate = useCallback((range, value) => {
    setState((prev) => ({
      ...prev,
      [range]: value ? Number(value) : ''
    }));
  }, []);

  return (
    <MultiSelectComponent
      searchTerm={state.searchTerm}
      onSelectAll={onSelectAll}
      options={state.filteredOptions}
      searchValue={state.searchValue}
      onSearch={onSearch}
      onOptionSelected={onOptionSelected}
      onClear={() => {}}
      optionsRenderer={props.cellRendererFramework}
      isNumericFilter={isNumericDataRef.current}
      startRange={state.startRange}
      endRange={state.endRange}
      numericOperator={state.numericOperator}
      numericOperatorUpdated={numericOpertaorUpdated}
      numericRangeUpdate={numericRangeUpdate}
      colDef={props.colDef}
    />
  );
};

export default MultiSelectFilter;
